<template>
  <div class="h3 mb-4">Лечение</div>

  <div class="paragraph-s_h text-secondary">
    Рекомендуется к заполнению при наличии медицинской лицензии или оздоровительных программ
  </div>

  <e-form ref="formRef" class="mt-10">
    <inputs-group
      title="Предоставляет ли ваш объект размещения лечебные, оздоровительные или медицинские услуги?"
      :required="!isQuestionnaireSent"
    >
      <e-radio-group v-model="form.hasTreatment" :items="hasTreatmentOptions" inline :disabled="isQuestionnaireSent" />
    </inputs-group>

    <e-alert v-if="form.hasTreatment === null && formRef?.isDirty" variant="error" class="mt-10">
      <div class="paragraph-m_h font-600">Ответьте на вопрос, чтобы продолжить</div>
      <p class="paragraph-s_h text-secondary">
        Если вы не готовы заполнить информацию в разделе сейчас, ответьте <b>«Нет»</b>
      </p>
    </e-alert>

    <div v-if="form.hasTreatment" class="mt-10">
      <link-nav v-model="activeTabIndex" :items="form.treatmentCategories" class="mb-10" />

      <component
        :is="form.treatmentCategories[activeTabIndex].type === 'dropdowns' ? TreatmentDropdowns : TreatmentCheckboxes"
        :key="activeTabIndex"
        :groups="form.treatmentCategories[activeTabIndex].groups"
        :label="form.treatmentCategories[activeTabIndex].label"
      />

      <link-nav
        v-model="activeTabIndex"
        :items="form.treatmentCategories"
        class="mb-10 mt-10"
        @update:model-value="onActiveTabIndexChange"
      />
    </div>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="onSubmit">Далее</e-btn>
    </div>

    <confirmation-dialog
      v-if="!isQuestionnaireSent"
      v-model="isDeleteTreatmentConfirmationDialogOpened"
      :action="hideTreatment"
      confirm-text="Удалить"
    >
      <div class="h3 mb-6">Удалить раздел «Лечение»</div>
      <div v-if="!questionnaire?.hasParent" class="paragraph-l text-secondary">
        Данные раздела «Лечение» сохранены в черновике и не будут отправлены на публикацию
      </div>
      <div v-else class="paragraph-l text-secondary">
        Раздел «Лечение» уже опубликован. Данные будут сняты с публикации после отправки анкеты, но останутся сохранены
        в черновике
      </div>
    </confirmation-dialog>
  </e-form>
</template>

<script lang="ts" setup>
import { nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue';
import type { EFormType } from '@/core/types/components/components';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import LinkNav from '@/core/components/interface/LinkNav.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAlert, EBtn, EForm, ERadioGroup } from '@/core/components/ui';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { QuestionnaireMasterStep } from '@/graphql/generated';
import TreatmentCheckboxes from '@/parts/content/components/treatment/TreatmentCheckboxes.vue';
import TreatmentDropdowns from '@/parts/content/components/treatment/TreatmentDropdowns.vue';
import { useContentStore } from '@/parts/content/store';

const {
  isQuestionnaireSent,
  questionnaire,
  back,
  nextStep,
  questionnaireAnswers,
  dictionaries,
  moveToNextStep,
  saveQuestionnaire,
  saveQuestionnaireDebounced,
} = useContentStore();

const activeTabIndex = ref(0);

const formRef = ref<EFormType>();

const form = reactive({
  hasTreatment: questionnaireAnswers.value?.treatment?.enabled ?? null,
  treatmentCategories:
    dictionaries.value?.treatment?.categories.map((category, index) => ({
      label: category.name,
      value: index,
      type: index === 4 ? 'checkboxes' : 'dropdowns',
      groups: category.groups.map((group, groupIndex) => ({
        name: group.name,
        isChecked:
          index === 4 ||
          !!questionnaireAnswers.value?.treatment?.treatmentCategories?.[index]?.groups[groupIndex].items.some(
            (item) => item.options.length,
          ),
        items: group.items.map((item, itemIndex) => ({
          name: item.name,
          options: item.options,
          selectedOptions:
            questionnaireAnswers.value?.treatment?.treatmentCategories?.[index]?.groups[groupIndex]?.items[itemIndex]
              ?.options || [],
        })),
      })),
    })) || [],
});

const isDeleteTreatmentConfirmationDialogOpened = ref(false);

let forseChangeHasTreatment = false;
const hideTreatment = () => {
  forseChangeHasTreatment = true;
  form.hasTreatment = false;

  void nextTick(() => {
    forseChangeHasTreatment = false;
  });
};

watch(
  () => form.hasTreatment,
  (newValue, oldValue) => {
    if (oldValue === true && !newValue && !forseChangeHasTreatment) {
      isDeleteTreatmentConfirmationDialogOpened.value = true;

      void nextTick(() => {
        form.hasTreatment = true;
      });
    }
  },
);

const onActiveTabIndexChange = () => {
  formRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const isLoading = ref(false);

const onSubmit = async () => {
  if (isQuestionnaireSent.value) {
    nextStep();
    return;
  }

  await formRef.value?.validate();

  if (form.hasTreatment === null) {
    return;
  }

  isLoading.value = true;

  await moveToNextStep(QuestionnaireMasterStep.Treatment, mapAnswersToSave());

  isLoading.value = false;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.treatment) {
    delete questionnaireAnswers.value.treatment.__typename;

    if (!compareObjects(mapAnswersToSave().treatment, questionnaireAnswers.value.treatment)) {
      return true;
    }
  }

  return false;
};

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());

watch(form, () => {
  void save();
});

const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

const hasTreatmentOptions = [
  { title: 'Да', value: true },
  { title: 'Нет', value: false },
];

function mapAnswersToSave() {
  return {
    treatment: {
      enabled: !!form.hasTreatment,
      treatmentCategories: form.treatmentCategories.map((category) => ({
        name: category.label,
        groups: category.groups.map((group) => ({
          name: group.name,
          items: group.isChecked
            ? group.items.map((item) => ({
                name: item.name,
                options: item.selectedOptions,
              }))
            : [],
        })),
      })),
    },
  };
}
</script>

<style scoped></style>
